import React, { useId } from 'react';

const formatNumber = (value) => {
  const num = Number(value) || 0;
  if (Math.abs(num) >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (Math.abs(num) >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return `${Math.round(num)}`;
};

const niceMax = (max) => {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const scaled = max / pow;
  let factor;
  if (scaled <= 1) factor = 1;
  else if (scaled <= 2) factor = 2;
  else if (scaled <= 5) factor = 5;
  else factor = 10;
  return factor * pow;
};

/**
 * Smooth area + line chart. data: [{ date, value }]
 */
export const LineChart = ({ data = [], color = '#6366f1', height = 240, valuePrefix = '', valueSuffix = '' }) => {
  const gradientId = useId();
  const width = 760;
  const padding = { top: 20, right: 16, bottom: 28, left: 44 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  if (!data.length) {
    return <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height }}>No data available</div>;
  }

  const maxValue = niceMax(Math.max(...data.map((d) => Number(d.value) || 0), 0));
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;

  const points = data.map((d, i) => {
    const x = padding.left + i * stepX;
    const y = padding.top + innerH - ((Number(d.value) || 0) / maxValue) * innerH;
    return { x, y, ...d };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${padding.top + innerH} L ${points[0].x.toFixed(1)} ${padding.top + innerH} Z`;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    value: maxValue * t,
    y: padding.top + innerH - t * innerH,
  }));

  const labelEvery = Math.ceil(data.length / 6);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {yTicks.map((tick, i) => (
        <g key={i}>
          <line x1={padding.left} y1={tick.y} x2={width - padding.right} y2={tick.y} stroke="#eef0f4" strokeWidth="1" />
          <text x={padding.left - 8} y={tick.y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">
            {formatNumber(tick.value)}
          </text>
        </g>
      ))}

      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

      {points.map((p, i) => (
        <g key={i}>
          {data.length <= 31 && <circle cx={p.x} cy={p.y} r="2.5" fill={color} />}
          {i % labelEvery === 0 && (
            <text x={p.x} y={height - 8} textAnchor="middle" fontSize="9" fill="#9ca3af">
              {String(p.date).slice(5)}
            </text>
          )}
          <title>{`${p.date}: ${valuePrefix}${formatNumber(p.value)}${valueSuffix}`}</title>
        </g>
      ))}
    </svg>
  );
};

/**
 * Horizontal bar chart. data: [{ label, value }]
 */
export const BarChart = ({ data = [], color = '#6366f1', height = 260, valuePrefix = '' }) => {
  if (!data.length) {
    return <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height }}>No data available</div>;
  }

  const maxValue = Math.max(...data.map((d) => Number(d.value) || 0), 1);

  return (
    <div className="space-y-3" style={{ minHeight: height }}>
      {data.map((d, i) => {
        const pct = ((Number(d.value) || 0) / maxValue) * 100;
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-28 shrink-0 text-xs text-gray-600 truncate" title={d.label}>{d.label}</div>
            <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
              <div
                className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: color }}
              >
                <span className="text-[10px] font-semibold text-white">{valuePrefix}{formatNumber(d.value)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Vertical bars (good for size distributions). data: [{ label, value }]
 */
export const ColumnChart = ({ data = [], color = '#0ea5e9', height = 220 }) => {
  const width = 760;
  const padding = { top: 16, right: 12, bottom: 28, left: 36 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  if (!data.length) {
    return <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height }}>No data available</div>;
  }

  const maxValue = niceMax(Math.max(...data.map((d) => Number(d.value) || 0), 0));
  const slot = innerW / data.length;
  const barW = Math.min(slot * 0.6, 48);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} preserveAspectRatio="xMidYMid meet">
      {[0, 0.5, 1].map((t, i) => {
        const y = padding.top + innerH - t * innerH;
        return (
          <g key={i}>
            <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#eef0f4" />
            <text x={padding.left - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">{formatNumber(maxValue * t)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const value = Number(d.value) || 0;
        const barH = (value / maxValue) * innerH;
        const x = padding.left + i * slot + (slot - barW) / 2;
        const y = padding.top + innerH - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={Math.max(barH, 1)} rx="4" fill={color}>
              <title>{`${d.label}: ${value}`}</title>
            </rect>
            <text x={x + barW / 2} y={height - 10} textAnchor="middle" fontSize="10" fill="#6b7280">{d.label}</text>
          </g>
        );
      })}
    </svg>
  );
};

const DONUT_COLORS = ['#6366f1', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#ec4899', '#14b8a6'];

/**
 * Donut chart with legend. data: [{ label, value }]
 */
export const DonutChart = ({ data = [], size = 200 }) => {
  const total = data.reduce((sum, d) => sum + (Number(d.value) || 0), 0);

  if (!total) {
    return <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height: size }}>No data available</div>;
  }

  const radius = size / 2;
  const inner = radius * 0.62;
  let cumulative = 0;

  const arcs = data.map((d, i) => {
    const value = Number(d.value) || 0;
    const startAngle = (cumulative / total) * Math.PI * 2;
    cumulative += value;
    const endAngle = (cumulative / total) * Math.PI * 2;
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

    const x1 = radius + radius * Math.sin(startAngle);
    const y1 = radius - radius * Math.cos(startAngle);
    const x2 = radius + radius * Math.sin(endAngle);
    const y2 = radius - radius * Math.cos(endAngle);
    const xi2 = radius + inner * Math.sin(endAngle);
    const yi2 = radius - inner * Math.cos(endAngle);
    const xi1 = radius + inner * Math.sin(startAngle);
    const yi1 = radius - inner * Math.cos(startAngle);

    const path = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${inner} ${inner} 0 ${largeArc} 0 ${xi1} ${yi1} Z`;
    return { path, color: DONUT_COLORS[i % DONUT_COLORS.length], label: d.label, value, pct: Math.round((value / total) * 100) };
  });

  return (
    <div className="flex items-center gap-6 flex-wrap">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        {arcs.map((arc, i) => (
          <path key={i} d={arc.path} fill={arc.color}>
            <title>{`${arc.label}: ${arc.value} (${arc.pct}%)`}</title>
          </path>
        ))}
        <text x={radius} y={radius - 4} textAnchor="middle" fontSize="22" fontWeight="700" fill="#111827">{formatNumber(total)}</text>
        <text x={radius} y={radius + 16} textAnchor="middle" fontSize="11" fill="#9ca3af">Total</text>
      </svg>
      <div className="space-y-2">
        {arcs.map((arc, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: arc.color }} />
            <span className="text-gray-700 capitalize">{arc.label}</span>
            <span className="text-gray-400">{arc.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Sparkline = ({ data = [], color = '#6366f1', width = 120, height = 36 }) => {
  if (!data.length) return null;
  const max = Math.max(...data.map((d) => Number(d.value) || 0), 1);
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const path = data
    .map((d, i) => {
      const x = i * stepX;
      const y = height - ((Number(d.value) || 0) / max) * height;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
